/**
 * Hook for managing slide export state and triggering exports.
 *
 * Handles:
 * - Building the slide configuration from selected content
 * - Auto-distributing plays into slides based on layout
 * - Calling the export API and triggering file download
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { secureJsonFetch } from '@/utils/SecureFetch'
import type { ExportSection as Section } from '@/components/playbook-editor/Types'
import type {
	SlideExportConfig,
	ExportSlide,
	MetadataOptions,
	SlideLayout,
	ExportFormat,
} from '@/types/SlideExport.types'
import { DEFAULT_METADATA_OPTIONS } from '@/types/SlideExport.types'

export interface UseSlideExportReturn {
	/** Selected section IDs */
	selectedSectionIds: Set<string>
	/** Toggle a section's selection */
	toggleSection: (sectionId: string) => void
	/** Select/deselect all sections */
	selectAllSections: () => void
	deselectAllSections: () => void

	/** Plays per slide */
	layout: SlideLayout
	setLayout: (layout: SlideLayout) => void

	/** Export file format */
	format: ExportFormat
	setFormat: (format: ExportFormat) => void

	/** Which metadata to show on slides */
	metadata: MetadataOptions
	toggleMetadata: (key: keyof MetadataOptions) => void

	/** Section divider toggle */
	includeDividers: boolean
	setIncludeDividers: (v: boolean) => void

	/** Computed slides based on current configuration */
	slides: ExportSlide[]
	/** Total selected play count */
	selectedPlayCount: number
	/** Whether the user has manually reorganized slides */
	isCustomized: boolean

	/** Move a play from one content slide to another */
	movePlay: (blockId: string, fromSlideIndex: number, toSlideIndex: number) => void
	/** Reset to auto-distributed layout */
	resetSlides: () => void

	/** Export state */
	isExporting: boolean
	exportError: string | null
	/** Trigger the export. Returns true on success, false on failure. */
	exportSlides: () => Promise<boolean>
}

export function useSlideExport(
	playbookId: string,
	playbookName: string,
	sections: Section[],
): UseSlideExportReturn {
	const [selectedSectionIds, setSelectedSectionIds] = useState<Set<string>>(
		() => new Set(sections.map((s) => s.id))
	)
	const [layout, setLayout] = useState<SlideLayout>(1)
	const [format, setFormat] = useState<ExportFormat>('pptx')
	const [metadata, setMetadata] = useState<MetadataOptions>(DEFAULT_METADATA_OPTIONS)
	const [includeDividers, setIncludeDividers] = useState(true)
	const [isExporting, setIsExporting] = useState(false)
	const [exportError, setExportError] = useState<string | null>(null)
	const [customSlides, setCustomSlides] = useState<ExportSlide[] | null>(null)

	const toggleSection = useCallback((sectionId: string) => {
		setSelectedSectionIds((prev) => {
			const next = new Set(prev)
			if (next.has(sectionId)) {
				next.delete(sectionId)
			} else {
				next.add(sectionId)
			}
			return next
		})
	}, [])

	const selectAllSections = useCallback(() => {
		setSelectedSectionIds(new Set(sections.map((s) => s.id)))
	}, [sections])

	const deselectAllSections = useCallback(() => {
		setSelectedSectionIds(new Set())
	}, [])

	const toggleMetadata = useCallback((key: keyof MetadataOptions) => {
		setMetadata((prev) => ({ ...prev, [key]: !prev[key] }))
	}, [])

	// Build auto-distributed slides from selected sections + layout
	const { autoSlides, selectedPlayCount } = useMemo(() => {
		const selectedSections = sections.filter((s) => selectedSectionIds.has(s.id))
		const allSlides: ExportSlide[] = []
		let playCount = 0

		for (const section of selectedSections) {
			if (section.plays.length === 0) continue

			if (includeDividers) {
				allSlides.push({ type: 'divider', title: section.name })
			}

			// Distribute plays into slides based on layout
			const plays = section.plays
			playCount += plays.length

			for (let i = 0; i < plays.length; i += layout) {
				const chunk = plays.slice(i, i + layout)
				allSlides.push({
					type: 'content',
					blockIds: chunk.map((p) => p.id),
				})
			}
		}

		return { autoSlides: allSlides, selectedPlayCount: playCount }
	}, [sections, selectedSectionIds, layout, includeDividers])

	// Reset custom slides when auto-distribution inputs change
	const prevAutoRef = useRef(autoSlides)
	useEffect(() => {
		if (prevAutoRef.current !== autoSlides) {
			prevAutoRef.current = autoSlides
			setCustomSlides(null)
		}
	}, [autoSlides])

	// Use custom slides if the user has dragged, otherwise auto-distributed
	const slides = customSlides ?? autoSlides
	const isCustomized = customSlides !== null

	const movePlay = useCallback((blockId: string, fromSlideIndex: number, toSlideIndex: number) => {
		if (fromSlideIndex === toSlideIndex) return

		setCustomSlides((prev) => {
			const current = prev ?? autoSlides
			const next: ExportSlide[] = current.map((slide) =>
				slide.type === 'content'
					? { ...slide, blockIds: [...slide.blockIds] }
					: { ...slide },
			)

			const fromSlide = next[fromSlideIndex]
			const toSlide = next[toSlideIndex]
			if (!fromSlide || fromSlide.type !== 'content' || !toSlide || toSlide.type !== 'content') return prev

			// Remove from source slide
			fromSlide.blockIds = fromSlide.blockIds.filter((id) => id !== blockId)

			// Add to target slide
			toSlide.blockIds.push(blockId)

			// Remove empty content slides
			return next.filter((s) => s.type === 'divider' || (s.type === 'content' && s.blockIds.length > 0))
		})
	}, [autoSlides])

	const resetSlides = useCallback(() => {
		setCustomSlides(null)
	}, [])

	const exportSlides = useCallback(async (): Promise<boolean> => {
		if (slides.length === 0) return false

		setIsExporting(true)
		setExportError(null)

		try {
			const config: SlideExportConfig = {
				title: playbookName,
				format,
				slides,
				metadata,
				includeSectionDividers: includeDividers,
			}

			const response = await secureJsonFetch('/api/export/slides', {
				method: 'POST',
				body: { playbookId, config },
			})

			if (!response.ok) {
				const errorText = await response.text()
				throw new Error(errorText || `Export failed (${response.status})`)
			}

			// Trigger browser download
			const blob = await response.blob()
			const extension = format === 'pptx' ? 'pptx' : 'pdf'
			const filename = `${playbookName.replace(/[^a-zA-Z0-9-_ ]/g, '')}.${extension}`

			const url = URL.createObjectURL(blob)
			const anchor = document.createElement('a')
			anchor.href = url
			anchor.download = filename
			anchor.click()
			URL.revokeObjectURL(url)
			return true
		} catch (err) {
			setExportError(err instanceof Error ? err.message : 'Export failed')
			return false
		} finally {
			setIsExporting(false)
		}
	}, [slides, playbookId, playbookName, format, metadata, includeDividers])

	return {
		selectedSectionIds,
		toggleSection,
		selectAllSections,
		deselectAllSections,
		layout,
		setLayout,
		format,
		setFormat,
		metadata,
		toggleMetadata,
		includeDividers,
		setIncludeDividers,
		slides,
		selectedPlayCount,
		isCustomized,
		movePlay,
		resetSlides,
		isExporting,
		exportError,
		exportSlides,
	}
}
